import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Validators } from "@angular/forms";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { ActivatedRoute } from "@angular/router";
import { cloneDeep, find, remove } from "lodash";
import { Observable } from "rxjs";
import { first } from "rxjs/operators";
import { apiURL } from "../../../../environments/environment";
import { CatalogCourseOverview } from "../../catalog/interfaces/catalog-course-overview.interface";
import { CourseOverviewDetail } from "../../catalog/interfaces/course-overview-detail.interface";
import { CustomerDataInterface } from "../../customer/interfaces/customer-data.interface";
import { PaymentCodeDataInterface } from "../../customer/interfaces/payment-code-data.interface";
import { PaymentModuleDataInterface } from "../../customer/interfaces/payment-module-data.interface";
import { ProgramInterface } from "../../customer/interfaces/programs/program.interface";
import { CustomerService } from "../../customer/services/customer.service";
import { PaymentModuleService } from "../../customer/services/payment-module.service";
import { ProgramService } from "../../customer/services/program.service";
import {
  CacheableMethod,
  EdtellDialogConfig,
  EdtellDialogData,
  LangUtils,
  LoginService,
} from "../../edtell-common";
import { ObjectName } from "../../edtell-common/edtell-admin/enums/object-name.enum";
import { LocalStorageService } from "../../edtell-common/edtell-caching/services/local-storage.service";
import { DialogActionType } from "../../edtell-common/edtell-portal/enums/dialog-action-type.enum";
import { StudentRelationship } from "../../edtell-common/edtell-portal/enums/student-relationship.enum";
import { NotificationService } from "../../edtell-common/edtell-portal/services/notification.service";
import { emailFormatValidator } from "../../edtell-common/edtell-utilities/validators/email-format-validator";
import { minMaxAgeValidator } from "../../edtell-common/edtell-utilities/validators/min-max-age.validator";
import { EdtellFormControl } from "../../edtell-common/srs-forms/classes/edtell-form-control";
import { EdtellFormGroup } from "../../edtell-common/srs-forms/classes/edtell-form-group.class";
import { SrsFormState } from "../../edtell-common/srs-forms/enumerations/form-state.enum";
import { ProviderInterface } from "../../providers/interfaces/provider.interface";
import { RegistrationContactMapping } from "../../registrations/interfaces/registration-contact-mapping.interface";
import { RegistrationContact } from "../../registrations/interfaces/registration-contact.interface";
import { RegistrationPayment } from "../../registrations/interfaces/registration-payment.interface";
import { RegistrationProfileData } from "../../registrations/interfaces/registration-profile.interface";
import { Registration } from "../../registrations/interfaces/registration.interface";
import { ReviewCoursesDialogComponent } from "../components/course-overview/review-courses-dialog/review-courses-dialog.component";
import { ShoppingCartMessageDialogComponent } from "../components/course-overview/shopping-cart-message-dialog/shopping-cart-message-dialog.component";
import { NcaaDialogComponent } from "../components/live-program-view/ncaa-dialog/ncaa-dialog.component";
import { ShoppingCartInfo } from "../interfaces/shopping-cart-info.interface";
import { RegConfirmEmailRequest } from "../../registrations/interfaces/reg-confirm-email-request.interface";
import { AddRegistrationPaymentResponse } from "../../registrations/interfaces/add-registration-payment-response.interface";
import { RegistrationDetail } from "../../registrations/interfaces/registration-detail.interface";
import { CustomerContactInterface } from "../../customer/interfaces/customer-contact.interface";
import { RegistrationProgramErrorDialogComponent } from "../components/registration-program-error-dialog/registration-program-error-dialog.component";
import { CourseInterface } from "../../catalog/interfaces/course.interface";

@Injectable({
  providedIn: "root",
})
export class ShoppingCartService {
  private ncaaStudent: boolean;
  private ncaaStudentId: string;
  private selectedCreditGroup: CourseOverviewDetail[];

  private shoppingCart = new Map<string, CourseOverviewDetail>();

  private courseOverview: CatalogCourseOverview;

  private school: CustomerDataInterface;

  private program: ProgramInterface;
  private registration: Registration;
  private details: RegistrationDetail[] = [];

  private formGroup: EdtellFormGroup = new EdtellFormGroup(SrsFormState.WRITE, {
    selectedDetail: new EdtellFormControl(null),
  });

  private schoolContactFormGroup;

  private paymentModule: PaymentModuleDataInterface;
  private contacts: RegistrationContact[] = [];
  private contactMappings: RegistrationContactMapping[] = [];

  private httpOptions: object;
  private textHttpOptions: object;

  private suppressPricing = false;

  constructor(
    public dialog: MatDialog,
    private notificationService: NotificationService,
    private localStorageService: LocalStorageService,
    private customerService: CustomerService,
    private paymentModuleService: PaymentModuleService,
    private programService: ProgramService,
    private http: HttpClient,
    private loginService: LoginService,
    private route: ActivatedRoute,
  ) {
    this.httpOptions = loginService.httpOptions;
    this.textHttpOptions = loginService.textHttpOptions;

    this.setUpShoppingCart();
  }

  async setUpShoppingCart() {
    const storageShoppingCart =
      this.localStorageService.getLocalStorageItem("shoppingCart");
    if (storageShoppingCart) {
      this.shoppingCart = storageShoppingCart;
    }
    await this.initShoppingCart();
    this.initContactForm();
    this.initSchoolContactForm();
  }

  async initShoppingCart() {
    let registrationProfile;
    // api returns 308 status if cache reg does not exist
    try {
      registrationProfile = await this.findRegistration()
        .pipe(first())
        .toPromise();
    } catch {
      // acceptable failure
    }
    this.registration = registrationProfile?.registration;
    this.contacts = registrationProfile?.contacts;
    if (this.registration == null) {
      this.shoppingCart = new Map();
      this.localStorageService.removeLocalStorageItem("shoppingCart");
      localStorage.removeItem("CARTID");
      return;
    } else {
      this.setNcaaStudent(this.registration.ncaaProspect);
      this.setNcaaStudentId(this.registration.ncaaStudentNumber);
    }

    if (this.registration.cartStatus == "Complete") {
      this.clearShoppingCart();
      return;
    }

    for (const contact of this.contacts) {
      this.contactMappings = [...this.contactMappings, ...contact.mappings];
    }
  }

  async getLiveProgramViewData(progamId?: number, schoolCode?: string) {
    const liveProgramViewData = await this.programService
      .getLiveProgramViewData(progamId, schoolCode)
      .pipe(first())
      .toPromise();
    this.program = liveProgramViewData.program;
    this.suppressPricing = !this.program.showCost;
    this.checkRegInCorrectProgram();
    return liveProgramViewData;
  }

  resetNcaaInfo() {
    this.ncaaStudent = null;
    this.ncaaStudentId = null;
  }

  checkRegInCorrectProgram() {
    if (this.registration == null) {
      return;
    }

    if (this.program?.id != this.registration.programId) {
      this.clearShoppingCart();
    }
  }

  getPaymentModule(paymentModuleId?: number) {
    console.log(this.program);
    if (paymentModuleId == null) {
      paymentModuleId = this.program.paymentModuleId;
    }

    if (paymentModuleId == 0) {
      console.error("PAYMENT MODULE ID IS 0");
      return;
    }

    return this.http.get<PaymentModuleDataInterface>(
      `${apiURL}/registrations-app/registrations/shopping-cart/payment-module?id=${paymentModuleId}`,
      this.httpOptions,
    );
  }

  async getCatalogCourseOverview(courseId, programId, schoolCode) {
    const catalogCourseOverview = await this.programService
      .getCatalogCourseOverview(courseId, programId, schoolCode)
      .pipe(first())
      .toPromise();
    this.program = catalogCourseOverview.program;
    return catalogCourseOverview;
  }

  setNcaaStudent(ncaaStudent: boolean) {
    this.ncaaStudent = ncaaStudent;
  }

  getNcaaStudent() {
    return this.ncaaStudent;
  }

  setNcaaStudentId(ncaaStudentId: string) {
    this.ncaaStudentId = ncaaStudentId;
  }

  getNcaaStudentId() {
    return this.ncaaStudentId;
  }

  openNcaaDialog(): Observable<EdtellDialogData> {
    return new Observable((obs) => {
      const dialogConfig: EdtellDialogConfig = {
        width: "600px",
        data: {
          action: DialogActionType.EDIT,
          objectData: {
            ncaa: this.ncaaStudent,
            ncaaId: this.ncaaStudentId,
          },
          securityObject: ObjectName.CUSTOMER_PROGRAM,
        },
        disableClose: true,
      };

      const dialogRef: MatDialogRef<NcaaDialogComponent> = this.dialog.open(
        NcaaDialogComponent,
        dialogConfig,
      );

      dialogRef
        .afterClosed()
        .subscribe(async (dialogData: EdtellDialogData) => {
          if (dialogData.action == DialogActionType.CANCEL) {
            obs.next(null);
          }
          this.setNcaaStudent(dialogData.objectData.ncaa);
          this.setNcaaStudentId(
            dialogData.objectData.ncaa ? dialogData.objectData.ncaaId : null,
          );
          await this.updateNcaaRegistrationInfo().pipe(first()).toPromise();
          obs.next(dialogData);
        });
    });
  }

  async changeNcaaStatus() {
    await this.openNcaaDialog().pipe(first()).toPromise();
    await this.openReviewCoursesDialog().pipe(first()).toPromise();
  }

  checkRegistrationError(): Observable<"new registration" | "cancel" | null> {
    return new Observable((obs) => {
      this.findRegistration().subscribe((regProfile) => {
        const registration = regProfile?.registration;

        if (registration == null && this.registration != null) {
          this.notificationService.notificationEmitter.emit({
            message: `Registration no longer exists.`,
          });
          obs.next("new registration");
        }

        if (registration != null) {
          const regProgramId = registration.programId;
          const programId = this.program.id;
          if (regProgramId != programId) {
            const dialogConfig: EdtellDialogConfig = {
              width: "600px",
              data: {
                action: DialogActionType.EDIT,
                objectData: {},
                securityObject: null,
              },
              disableClose: true,
            };

            const dialogRef: MatDialogRef<RegistrationProgramErrorDialogComponent> =
              this.dialog.open(
                RegistrationProgramErrorDialogComponent,
                dialogConfig,
              );
            dialogRef.afterClosed().subscribe((dialogData) => {
              obs.next(dialogData ? "new registration" : "cancel");
            });
          } else {
            obs.next(null);
          }
        } else {
          obs.next(null);
        }
      });
    });
  }

  async addCourse() {
    const complete = await this.registrationIsComplete()
      .pipe(first())
      .toPromise();

    if (complete) {
      return;
    }

    const errorResponse = await this.checkRegistrationError()
      .pipe(first())
      .toPromise();
    if (errorResponse == "cancel") {
      return;
    }
    if (errorResponse == "new registration") {
      this.clearShoppingCart();
    }

    // there may have been courses added in other tabs
    await this.checkRegistrationForChanges().pipe(first()).toPromise();

    const detail = cloneDeep(this.getSelectedDetail());
    const courseNumber = detail.courseType + detail.courseId;
    if (this.shoppingCart.has(courseNumber)) {
      this.notificationService.notificationEmitter.emit({
        message: `A course with the Course Number ${courseNumber} is already in your shopping cart`,
      });
      return;
    }
    detail.syllabus = null;

    const course = detail.courseType + detail.courseId;

    this.shoppingCart.set(course, detail);

    if (this.registration == null) {
      const dialogData = await this.openNcaaDialog().pipe(first()).toPromise();
      if (dialogData == null) {
        this.shoppingCart.delete(course);
        return;
      }
      await this.initRegistration().pipe(first()).toPromise();
    }

    await this.getSchool().pipe(first()).toPromise();

    this.localStorageService.setLocalStorageItem(
      "shoppingCart",
      this.shoppingCart,
    );

    if (this.registration.ncaaProspect == null) {
      this.openNcaaDialog().subscribe(async (dialogData) => {
        await this.addShoppingCartRegistrationDetail(detail)
          .pipe(first())
          .toPromise();
        this.openReviewCoursesDialog().subscribe(() => {});
      });
    } else {
      await this.addShoppingCartRegistrationDetail(detail)
        .pipe(first())
        .toPromise();
      this.openReviewCoursesDialog().subscribe(() => {});
    }
  }

  async removeCourse(key: string) {
    const detail = this.shoppingCart.get(key);
    await this.deleteRegistrationDetail(
      this.registration.id,
      detail.courseType + detail.courseId,
    )
      .pipe(first())
      .toPromise();
    this.shoppingCart.delete(key);
    this.localStorageService.setLocalStorageItem(
      "shoppingCart",
      this.shoppingCart,
    );
  }

  registrationIsComplete(): Observable<boolean> {
    return new Observable((obs) => {
      const message =
        "It appears your registration has already been completed.  Clear your browsing history or try another browser.  Please call Educere at 866-433-8237 for more information.";
      if (this.registration?.id == null) {
        obs.next(false);
        return;
      }
      this.isRegistrationComplete().subscribe(async (isComplete) => {
        if (isComplete) {
          this.registration.cartStatus = "Complete";
          this.openMessageDialog(message).pipe(first()).toPromise();
        }
        obs.next(isComplete);
      });
    });
  }

  openReviewCoursesDialog(): Observable<EdtellDialogData> {
    console.log(this);
    return new Observable((obs) => {
      const dialogConfig: EdtellDialogConfig = {
        width: "600px",
        data: {
          action: DialogActionType.EDIT,
          objectData: {},
          securityObject: ObjectName.CUSTOMER_PROGRAM,
        },
      };

      const dialogRef: MatDialogRef<ReviewCoursesDialogComponent> =
        this.dialog.open(ReviewCoursesDialogComponent, dialogConfig);

      dialogRef
        .afterClosed()
        .subscribe(async (dialogData: EdtellDialogData) => {
          if (dialogData?.action == DialogActionType.CANCEL) {
            // await this.updateRegistrationStatus("Shopping").pipe(first()).toPromise();
          }
          obs.next(dialogData);
        });
    });
  }

  openMessageDialog(message: string): Observable<void> {
    return new Observable((obs) => {
      const dialogConfig: EdtellDialogConfig = {
        width: "400px",
        data: {
          action: DialogActionType.VIEW,
          objectData: {
            message: message,
          },
          securityObject: ObjectName.REGISTRATIONS,
        },
      };

      const dialogRef: MatDialogRef<ShoppingCartMessageDialogComponent> =
        this.dialog.open(ShoppingCartMessageDialogComponent, dialogConfig);

      dialogRef
        .afterClosed()
        .subscribe(async (dialogData: EdtellDialogData) => {
          obs.next(null);
        });
    });
  }

  getSelectedDetail(): CourseOverviewDetail {
    return this.formGroup.get("selectedDetail").value;
  }

  setSelectedCreditGroup(details: CourseOverviewDetail[]) {
    this.selectedCreditGroup = details;
  }

  getSelectedCreditGroup() {
    return this.selectedCreditGroup;
  }

  getFormGroup() {
    return this.formGroup;
  }

  getSchoolContactFormGroup() {
    return this.schoolContactFormGroup;
  }

  getShoppingCart() {
    return this.shoppingCart;
  }

  getTotalPriceOfShoppingCart() {
    if (this.suppressPricing) {
      return 0;
    }

    let price = 0;

    for (const detail of Array.from(this.shoppingCart.values())) {
      price += detail.price;
    }
    return price;
  }

  getCourseOverview() {
    return this.courseOverview;
  }

  setCourseOverview(courseOverview: CatalogCourseOverview) {
    this.courseOverview = courseOverview;
    this.suppressPricing = !courseOverview.program.showCost;
  }

  getSchool(): Observable<CustomerDataInterface> {
    return new Observable((obs) => {
      if (this.school != undefined) {
        obs.next(this.school);
        return;
      }

      if (this.shoppingCart.size < 1) {
        obs.next(undefined);
        return;
      }

      // get first item in map
      const iterator = this.shoppingCart.entries();
      const firstIteration = iterator.next();
      const course = firstIteration.value[1];

      this.customerService
        .getCustomer(course.customerId)
        .subscribe((customer) => {
          this.school = customer;
          obs.next(customer);
        });
    });
  }

  getSuppressPricing() {
    return this.suppressPricing;
  }

  setSuppressPricing(suppress: boolean) {
    this.suppressPricing = suppress;
  }

  findRegistration(): Observable<RegistrationProfileData> {
    return this.http.get<RegistrationProfileData>(
      `${apiURL}/registrations-app/registrations/shopping-cart/registration`,
      this.httpOptions,
    );
  }

  getCourses(courseIds: number[]) {
    return this.http.get<CourseInterface[]>(
      `${apiURL}/customer-app/customer/program/live-view/active-courses?courseIds=${courseIds}`,
      this.httpOptions,
    );
  }

  updateRegistrationInformation(
    registration: RegistrationProfileData,
  ): Observable<RegistrationProfileData> {
    return this.http.put<RegistrationProfileData>(
      `${apiURL}/registrations-app/registrations/shopping-cart/registration/information`,
      registration,
      this.httpOptions,
    );
  }

  updateRegistration(registration: Registration): Observable<Registration> {
    return this.http.put<Registration>(
      `${apiURL}/registrations-app/registrations/shopping-cart/registration`,
      registration,
      this.httpOptions,
    );
  }

  getCustomerPaymentCodes(customerId: number) {
    return this.http.get<PaymentCodeDataInterface[]>(
      `${apiURL}/registrations-app/registrations/shopping-cart/payment-codes?customerId=${customerId}`,
      this.httpOptions,
    );
  }

  createRegistrationPayment(payment: RegistrationPayment) {
    return this.http.post<boolean>(
      `${apiURL}/registrations-app/registrations/shopping-cart/payment`,
      payment,
      this.httpOptions,
    );
  }

  checkAuthNetCredentials(paymentModuleId: number) {
    return this.http.get<any>(
      `${apiURL}/registrations-app/registrations/shopping-cart/check-credentials?paymentModuleId=${paymentModuleId}`,
      this.httpOptions,
    );
  }

  updateRegistrationDetails(details: RegistrationDetail[]) {
    return this.http.put<RegistrationDetail[]>(
      `${apiURL}/registrations-app/shopping-cart/details`,
      details,
      this.httpOptions,
    );
  }

  isRegistrationComplete(regId?: number) {
    if (regId == null) {
      regId = this.registration.id;
    }
    return this.http.get<boolean>(
      `${apiURL}/registrations-app/registrations/shopping-cart/is-complete?regId=${regId}`,
      this.httpOptions,
    );
  }

  getCurrentShoppingCart(regId?: number) {
    if (regId == null) {
      regId = this.registration.id;
    }
    return this.http.get<ShoppingCartInfo>(
      `${apiURL}/registrations-app/registrations/shopping-cart/current?regId=${regId}`,
      this.httpOptions,
    );
  }

  getCustomerContacts() {
    return this.http.get<CustomerContactInterface[]>(
      `${apiURL}/registrations-app/registrations/shopping-cart/customer/contacts`,
      this.httpOptions,
    );
  }

  isValidPaymentCode(paymentCode: string) {
    return this.http.get<boolean>(
      `${apiURL}/registrations-app/registrations/shopping-cart/payment-code/valid?paymentCode=${paymentCode}`,
      this.httpOptions,
    );
  }

  getEducereCustomerId() {
    return this.http.get<number>(
      `${apiURL}/registrations-app/registrations/shopping-cart/educere-customer-id`,
      this.httpOptions,
    );
  }

  @CacheableMethod()
  validateSchoolCodeAndProgramId(schoolCode: string, programId?: string) {
    let url = `${apiURL}/registrations-app/registrations/shopping-cart/validate-school-code?schoolCode=${schoolCode}`;

    if (programId != null) {
      url += `&programId=${programId}`;
    }
    return this.http.get<boolean>(url);
  }

  /*
        returns whether or not the registration is complete
    */
  checkRegistrationForChanges(): Observable<boolean> {
    return new Observable((obs) => {
      this.detectedShoppingCartChanges = false;
      if (this.registration == null) {
        obs.next(false);
        return;
      }
      this.getCurrentShoppingCart().subscribe((shoppingCartInfo) => {
        const registration = shoppingCartInfo.registrationProfile?.registration;

        for (const detail of shoppingCartInfo.courseDetails) {
          const courseNumber = detail.courseType + detail.courseId;
          // check if detail does not exist in shopping cart
          if (!this.shoppingCart.has(courseNumber)) {
            this.shoppingCart.set(courseNumber, detail);
            this.detectedShoppingCartChanges = true;
          }
        }

        // check if shopping cart item does not exist on registration
        for (const key of this.shoppingCart.keys()) {
          let hasDetail = false;
          for (const detail of shoppingCartInfo.courseDetails) {
            if (key == detail.courseType + detail.courseId) {
              hasDetail = true;
              break;
            }
          }
          if (!hasDetail) {
            this.shoppingCart.delete(key);
            this.detectedShoppingCartChanges = true;
          }
        }

        this.registration = registration;
        const isComplete = registration?.cartStatus == "Complete";
        obs.next(isComplete);
      });
    });
  }

  private detectedShoppingCartChanges = false;
  getDetectedShoppingCartChanges() {
    return this.detectedShoppingCartChanges;
  }

  initRegistration(): Observable<any> {
    return new Observable((obs) => {
      this.getSchool().subscribe((school) => {
        const registration = {
          id: 0,
          ncaaProspect: this.ncaaStudent ? true : false,
          ncaaStudentNumber: this.ncaaStudentId,
          customerId: school.id,
          cartStatus: "Shopping",
          programId: this.program.id,
        };

        let urlParams = `?ncaaProspect=${registration.ncaaProspect}`;

        urlParams += this.ncaaStudentId
          ? `&ncaaStudentNumber=${this.ncaaStudentId}`
          : "";
        urlParams += `&programId=${registration.programId}&schoolId=${registration.customerId}`;

        this.http
          .post<Registration>(
            `${apiURL}/registrations-app/registrations/shopping-cart/init${urlParams}`,
            null,
            this.httpOptions,
          )
          .subscribe((resp) => {
            this.registration = resp;
            obs.next(resp);
          });
      });
    });
  }

  addShoppingCartRegistrationDetail(detail: CourseOverviewDetail) {
    return new Observable((obs) => {
      this.http
        .post<RegistrationDetail>(
          `${apiURL}/registrations-app/registrations/shopping-cart/registration/detail?regId=${this.registration.id}&courseId=${detail.courseId}&programId=${detail.programId}&schoolCode=${this.school.schoolCode}&courseType=${detail.courseType}`,
          null,
          this.httpOptions,
        )
        .subscribe((detail) => {
          this.details.push(detail);
          obs.next(detail);
        });
    });
  }

  deleteRegistrationDetail(regId: number, courseNumber: string) {
    return new Observable((obs) => {
      this.http
        .delete<boolean>(
          `${apiURL}/registrations-app/registrations/shopping-cart/registration/detail?regId=${regId}&courseNumber=${courseNumber}`,
          this.httpOptions,
        )
        .subscribe((success) => {
          if (success) {
            remove(this.details, { courseNumber: courseNumber });
            obs.next(success);
          }
        });
    });
  }

  updateNcaaRegistrationInfo() {
    return new Observable((obs) => {
      if (this.registration == null) {
        obs.next(null);
        return;
      }

      if (
        this.registration.ncaaProspect == this.ncaaStudent &&
        this.registration.ncaaStudentNumber == this.ncaaStudentId
      ) {
        // console.log("No need for API")
        obs.next(this.registration);
        return;
      }

      this.registration.ncaaProspect = this.ncaaStudent;
      this.registration.ncaaStudentNumber = this.ncaaStudentId;

      this.updateRegistration(this.registration).subscribe((registration) => {
        // console.log("reg update")
        this.registration = registration;
        obs.next(this.registration);
      });
    });
  }

  setRegistrationStatusToCheckout() {
    return this.http.put<Registration>(
      `${apiURL}/registrations-app/registrations/shopping-cart/status/checkout`,
      null,
      this.httpOptions,
    );
  }

  updateRegistrationSchoolContactInfo() {
    return new Observable((obs) => {
      const schoolContact =
        this.schoolContactFormGroup.get("schoolContact").value;
      if (this.registration.schoolContactId == schoolContact) {
        // console.log("No need for API")
        obs.next(this.registration);
        return;
      }

      this.registration.schoolContactId = schoolContact;

      this.updateRegistration(this.registration).subscribe((registration) => {
        // console.log("reg update")
        this.registration = registration;
        obs.next(this.registration);
      });
    });
  }

  updateLicenseValueOnAllDetails() {
    return new Observable((obs) => {
      this.http
        .put<
          RegistrationDetail[]
        >(`${apiURL}/registrations-app/registrations/shopping-cart/registration/detail/license?regId=${this.registration.id}`, null, this.httpOptions)
        .subscribe((details) => {
          this.details = details;
          obs.next(this.details);
        });
    });
  }

  updateRegistrationContactInformation() {
    return new Observable((obs) => {
      const studentForm = this.contactInfoFormGroup.getRawValue().student;
      const shippingAddressForm = studentForm.shippingAddress;
      const shippingAddress = {
        shipToAddress1: shippingAddressForm.address1,
        shipToAddress2: shippingAddressForm.address2,
        shipToCity: shippingAddressForm.city,
        shipToCountry: shippingAddressForm.country,
        shipToState: shippingAddressForm.state,
        shipToZip: shippingAddressForm.zip,
        shipSameAsHome: shippingAddressForm.sameAsHomeAddress,
      };
      const birthdate = new Date(
        studentForm.birthdateYear,
        studentForm.birthdateMonth - 1,
        studentForm.birthdateDay,
      );

      this.registration = {
        ...this.registration,
        ...studentForm,
        ...studentForm.homeAddress,
        birthdate: birthdate.toISOString().split("T")[0],
        ...shippingAddress,
      };

      const contacts = [this.contactInfoFormGroup.get("parentOne").value];
      const parentTwo = this.contactInfoFormGroup.get("parentTwo").value;
      if (parentTwo.firstName != "" && parentTwo.firstName != null) {
        contacts.push(parentTwo);
      }

      const registrationProfile: RegistrationProfileData = {
        registration: this.registration,
        contacts: contacts,
        paymentCodeInfo: null,
        payments: [],
        dropMappings: [],
      };

      this.updateRegistrationInformation(registrationProfile).subscribe(
        (resp) => {
          this.registration = resp.registration;
          this.contacts = resp.contacts;
          // console.log("reg update reg", this.registration)
          // console.log("contacts", this.contacts)
          this.initContactForm();
          obs.next(resp);
          return;
        },
      );
    });
  }

  clearShoppingCart() {
    this.resetNcaaInfo();
    this.localStorageService.removeLocalStorageItem("shoppingCart");
    this.registration = null;
    this.paymentModule = null;
    this.contacts = [];
    this.initContactForm();
    this.initSchoolContactForm();
    this.shoppingCart.clear();
    window.localStorage.removeItem("CARTID");
  }

  //#region CONTACT FORM GROUP

  private contactInfoFormGroup: EdtellFormGroup;

  initContactForm() {
    const dateParts: string[] = this.registration?.birthdate?.split("-") ?? [];
    const jsDate: Date =
      dateParts.length === 3
        ? new Date(+dateParts[0], +dateParts[1] - 1, +dateParts[2].substr(0, 2))
        : null;

    const birthdateMonth: number =
      jsDate != null ? jsDate.getMonth() + 1 : null;
    const studentFormGroup = new EdtellFormGroup(SrsFormState.WRITE, {
      firstName: new EdtellFormControl(
        this.registration?.firstName,
        Validators.required,
      ),
      middleInitial: new EdtellFormControl(this.registration?.middleInitial),
      lastName: new EdtellFormControl(
        this.registration?.lastName,
        Validators.required,
      ),
      birthdateMonth: new EdtellFormControl(birthdateMonth, [
        Validators.required,
      ]),
      birthdateDay: new EdtellFormControl(jsDate?.getDate(), [
        Validators.required,
      ]),
      birthdateYear: new EdtellFormControl(jsDate?.getFullYear(), [
        Validators.required,
      ]),
      gradeLevel: new EdtellFormControl(
        this.registration?.gradeLevel ? +this.registration.gradeLevel : null,
        Validators.required,
      ),
      mobilePhone: new EdtellFormControl(this.registration?.mobilePhone),
      homePhone: new EdtellFormControl(this.registration?.homePhone, [
        Validators.required,
      ]),
      email: new EdtellFormControl(this.registration?.email, [
        Validators.required,
        emailFormatValidator(),
      ]),
      schoolEmail: new EdtellFormControl(null),
      homeAddress: this.createAddressFormGroup("Home"),
      shippingAddress: this.createAddressFormGroup("Shipping"),
    });

    let parentOne: RegistrationContact;
    let parentTwo: RegistrationContact;

    if (this.contacts?.length > 0) {
      parentOne = this.contacts[0];
      if (this.contacts.length > 1) {
        parentTwo = this.contacts[1];
      }
    }

    this.contactInfoFormGroup = new EdtellFormGroup(SrsFormState.WRITE, {
      student: studentFormGroup,
      parentOne: this.createParentFormGroup(1, parentOne),
      parentTwo: this.createParentFormGroup(2, parentTwo),
    });
  }

  initSchoolContactForm() {
    this.schoolContactFormGroup = new EdtellFormGroup(SrsFormState.WRITE, {
      schoolContact: new EdtellFormControl(this.registration?.schoolContactId),
    });
  }

  createAddressFormGroup(type: "Home" | "Shipping") {
    return new EdtellFormGroup(SrsFormState.WRITE, {
      address1: new EdtellFormControl(
        type == "Home"
          ? this.registration?.address1
          : this.registration?.shipToAddress1,
        [Validators.required, Validators.maxLength(100)],
      ),
      address2: new EdtellFormControl(
        type == "Home"
          ? this.registration?.address2
          : this.registration?.shipToAddress2,
        Validators.maxLength(100),
      ),
      city: new EdtellFormControl(
        type == "Home"
          ? this.registration?.city
          : this.registration?.shipToCity,
        Validators.required,
      ),
      state: new EdtellFormControl(
        type == "Home"
          ? this.registration?.state
          : this.registration?.shipToState,
        Validators.required,
      ),
      zip: new EdtellFormControl(
        type == "Home" ? this.registration?.zip : this.registration?.shipToZip,
        Validators.required,
      ),
      country: new EdtellFormControl(
        type == "Home"
          ? this.registration?.country
          : this.registration?.shipToCountry,
        Validators.required,
      ),
      sameAsHomeAddress: new EdtellFormControl(
        type == "Home" ? false : this.registration?.shipSameAsHome,
      ), // only used on shipping address
    });
  }

  createParentFormGroup(parent: 1 | 2, contact?: RegistrationContact) {
    const validators = parent == 1 ? [Validators.required] : [];
    let mapping: RegistrationContactMapping;
    if (contact != null) {
      mapping = find(contact.mappings, { regContactId: contact?.id });
    }
    return new EdtellFormGroup(SrsFormState.WRITE, {
      id: new EdtellFormControl(contact?.id),
      dateCreated: new EdtellFormControl(contact?.dateCreated),
      dateModified: new EdtellFormControl(contact?.dateModified),
      owner: new EdtellFormControl(contact?.owner),
      createdBy: new EdtellFormControl(contact?.createdBy),
      modifiedBy: new EdtellFormControl(contact?.modifiedBy),
      firstName: new EdtellFormControl(mapping?.firstName, validators),
      middleInitial: new EdtellFormControl(mapping?.middleInitial),
      lastName: new EdtellFormControl(mapping?.lastName, validators),
      contactRelationshipType: new EdtellFormControl(
        mapping?.contactRelationshipType,
        validators,
      ),
      mobilePhone: new EdtellFormControl(mapping?.mobilePhone),
      homePhone: new EdtellFormControl(mapping?.homePhone, [...validators]),
      email: new EdtellFormControl(contact?.email, [
        ...validators,
        emailFormatValidator(),
      ]),
      mappings: new EdtellFormControl(contact?.mappings),
    });
  }

  getContactInfoFormGroup() {
    return this.contactInfoFormGroup;
  }

  getRegistration() {
    return this.registration;
  }

  getDetails() {
    return this.details;
  }

  get currShoppingCart() {
    return this.shoppingCart;
  }

  getProgram() {
    return this.program;
  }

  getFakeFormGroupForTest() {
    this.contactInfoFormGroup.get("student").setValue({
      firstName: "John",
      middleInitial: "H",
      lastName: "Doe",
      gender: "M",
      birthdate: "1994-11-12",
      gradeLevel: 1,
      mobilePhone: "3025555555",
      homePhone: "6095556666",
      email: "john.doe@gmail.com",
      schoolEmail: null,
      homeAddress: {
        address1: "42 Wallaby Way",
        address2: null,
        city: "Sydeny",
        state: "TX",
        zip: "88503",
        country: "US",
        sameAsHomeAddress: null,
      },
      shippingAddress: {
        address1: "42 Wallaby Way",
        address2: null,
        city: "Sydeny",
        state: "TX",
        zip: "88503",
        country: "US",
        sameAsHomeAddress: null,
      },
    });
    this.contactInfoFormGroup.get("parentOne").setValue({
      firstName: "Jane",
      middleInitial: null,
      lastName: "Doe",
      relationshipToStudent: StudentRelationship.PARENT_GUARDIAN,
      homePhone: "4455556666",
      mobilePhone: null,
      email: "jane.doe@gmail.com",
    });
    this.contactInfoFormGroup.get("parentTwo").setValue({
      firstName: "John",
      middleInitial: "J",
      lastName: "Doe",
      relationshipToStudent: StudentRelationship.PARENT_GUARDIAN,
      homePhone: "4455556666",
      mobilePhone: "4566546454",
      email: "john.doe@gmail.com",
    });

    return this.contactInfoFormGroup;
  }

  //#endregion CONTACT FORM GROUP
}
